import { useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import SearchSelectComponent from "../../components/SearchSelectComponent";
import useValidationSchema, { FieldConfig } from "../../components/Validations";
import Toast from "../../helpers/Toast";
import { ClientCreateDTO } from "../../models/Client/ClientCreateDTO";
import { ClientDTO } from "../../models/Client/ClientDTO";
import { ClientService } from "../../services/ClientService";
import { CountryService } from "../../services/CountryService";
import { SelectItemsProps } from "./ClientEdit";
import { ClientAddressService } from "../../services/ClientAddressService";

export default function ClientCreate() {
  const intl = useIntl();
  const clientService = new ClientService();
  const [loadingCreate, setLoadingCreate] = useState<boolean>(false);
  const [client, setClient] = useState<ClientDTO>({} as ClientDTO);

  const [countrySelect, setCountrySelect] = useState<SelectItemsProps | null>(
    null
  );
  const countryService = new CountryService();

  const navigate = useNavigate();

  const fields: FieldConfig[] = [
    ['name', 'string', 'Screens.Client.Create.Validation.NameRequired'],
    ['VatNumber', 'NIF', 'Screens.Client.Create.Validation.VatNumberRequired'],
    ['PhoneNumber', 'string', 'Screens.Client.Create.Validation.PhoneNumberRequired'],
    ['Email', 'email', 'Screens.Client.Create.Validation.EmailRequired'],
    ['IdCountry', 'number', 'Screens.Client.Create.Validation.CountryRequired']
  ];

  const schemaClientValidation = useValidationSchema(fields);
  const clientAddressService = new ClientAddressService();

  const createClient = async () => {
    setLoadingCreate(true);

    const createClient: ClientCreateDTO = {
      name: client.name ?? null,
      erpId: client.erpId || "0",
      PhoneNumber: client.phoneNumber,
      Email: client.email,
      PaymentType: client.paymentConditions || "",
      VatNumber: client.vatNumber || "",
      Website: client.website || "",
      IdCountry: Number(countrySelect?.value),
      Street: client.address || "",
      Street2: client.address2 || "",
      City: client.locality || "",
      PostalCode: client.postalCode || "",
    };

    const { error } = schemaClientValidation.validate(createClient);
    if (error) {
      Toast.Show("error", error.details[0].message);
      setLoadingCreate(false);
      return;
    }

    const response = await clientService.CreateClient(createClient);
    if (response.success !== true) {
      Toast.Show("error", response.message);
      setLoadingCreate(false);
      return;
    }

    const idClient = response.obj;

    try {
      if (client.address || client.address2 || client.locality || client.postalCode) {
        const addressData = {
          clientId: idClient,
          street1: client.address || "",
          street2: client.address2 || "",
          city: client.locality || "",
          postalCode: client.postalCode || "",
          countryId: Number(countrySelect?.value) || 0, 
          active: true,
        };


        const addressResponse = await clientAddressService.create(addressData);

        if (!addressResponse.success) {
          Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.CreateErrorAuto" }));
        }
      }
    } catch (err) {
      Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.CreateError" }));
    }

    Toast.Show("success", response.message);
    setLoadingCreate(false);
    navigate(-1);
  };

  const searchCountry = async (nameCountry: string): Promise<SelectItemsProps[]> => {
    const response = await countryService.SearchCountryByName(nameCountry);
    if (response.success === false || response.obj === null) {
      Toast.Show("error", response.message)
    }

    const list: SelectItemsProps[] = [];

    response.obj?.forEach((element) => {
      const item: SelectItemsProps = {
        value: element.id.toString(),
        label: element.name
      }
      list.push(item)
    });
    return list;
  };

  return (
    <Layout>
      <div className="pageContainer">
        <div className="pageHeader">
          <PageTitle title={"Novo Cliente"} allowGoBack={true} />
        </div>
        <div className="pageBody">
          <label style={{ margin: "0px", padding: "13px" }}>
            <b>Dados do cliente</b>
          </label>
          <Row
            style={{
              margin: "0px",
              padding: "0px",
              alignItems: "flex-end",
              alignContent: "flex-end",
            }}
          >
            <Row>
              <InputComponent
                xl={6}
                lg={12}
                md={12}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"text"}
                value={client.name}
                label={intl.formatMessage({ id: "Screens.Client.Info.Name" })}
                readOnly={false}
                onChange={(t: string) => {
                  setClient({ ...client, name: t })
                }}
                required
              />
            </Row>
            <Row>
              <InputComponent
                xl={2}
                lg={4}
                md={2}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"text"}
                value={client.vatNumber}
                label={intl.formatMessage({ id: "Screens.Client.Info.NIF" })}
                readOnly={false}
                onChange={(t: string) => {
                  setClient({ ...client, vatNumber: t.toUpperCase() })
                }}
                placeholder={"PT999999999"}
                required
              />
              <InputComponent
                xl={2}
                lg={4}
                md={5}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"text"}
                value={client.phoneNumber}
                label={intl.formatMessage({
                  id: "Screens.Client.Info.PhoneNumber",
                })}
                readOnly={false}
                onChange={(t: string) => {
                  setClient({ ...client, phoneNumber: t.trim() !== "" ? t : client.phoneNumber })
                }}
                required
              />
              <InputComponent
                xl={2}
                lg={4}
                md={5}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"text"}
                value={client.email}
                label={intl.formatMessage({ id: "Screens.Client.Info.Email" })}
                readOnly={false}
                onChange={(t: string) => {
                  setClient({ ...client, email: t })
                }}
                required
              />
            </Row>
            <Row>
              <SearchSelectComponent
                xl={2}
                lg={4}
                md={5}
                sm={12}
                xs={12}
                label={intl.formatMessage({
                  id: "Screens.Client.Info.NameCountry",
                })}
                loadOptions={searchCountry}
                onChange={(e: SelectItemsProps) => {
                  setCountrySelect(e);
                }}
                value={countrySelect}
                style={{ width: "100%" }}
                required
              />
              <InputComponent
                xl={2}
                lg={4}
                md={5}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"text"}
                value={client?.locality}
                label={intl.formatMessage({ id: "Screens.Client.Info.City" })}
                readOnly={false}
                onChange={(t: string) => {
                  setClient({ ...client, locality: t })
                }}
              />
              <InputComponent
                xl={2}
                lg={4}
                md={2}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"text"}
                value={client?.postalCode}
                label={intl.formatMessage({
                  id: "Screens.Client.Info.PostalCode",
                })}
                readOnly={false}
                onChange={(t: string) => {
                  setClient({ ...client, postalCode: t })
                }}
              />
            </Row>
            <Row>
              <InputComponent
                xl={3}
                lg={6}
                md={6}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"text"}
                value={client?.address}
                label={intl.formatMessage({ id: "Screens.Client.Info.Street" })}
                readOnly={false}
                onChange={(t: string) => {
                  setClient({ ...client, address: t });
                  //console log client?.address2
                  console.log("Address2´2:", client?.address);
                }}
              />
              <InputComponent
                xl={3}
                lg={6}
                md={6}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"text"}
                value={client?.address2}
                label={intl.formatMessage({
                  id: "Screens.Client.Info.Street2",
                })}
                readOnly={false}
                onChange={(t: string) => {
                  setClient({ ...client, address2: t })
                }}
              />
            </Row>
            <Row>
              <InputComponent
                xl={3}
                lg={6}
                md={6}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"text"}
                value={client?.paymentConditions}
                label={intl.formatMessage({
                  id: "Screens.Client.Info.PaymentType",
                })}
                readOnly={false}
                onChange={(t: string) => {
                  setClient({ ...client, paymentConditions: t })
                }}
              />
              <InputComponent
                xl={3}
                lg={6}
                md={6}
                sm={12}
                xs={12}
                style={{ width: "100%" }}
                type={"text"}
                value={client?.website}
                label={intl.formatMessage({
                  id: "Screens.Client.Info.WebSite",
                })}
                readOnly={false}
                onChange={(t: string) => {
                  setClient({ ...client, website: t })
                }}
              />
            </Row>
          </Row>
          <Row style={{ margin: "0px", marginTop: "10px", padding: "0px" }}>
            <Col xl={1} lg={1} md={1} sm={1} xs={1}>
              <ButtonComponent
                disabled={loadingCreate}
                loading={loadingCreate}
                text={intl.formatMessage({ id: "ClientCreate.Save" })}
                onClick={createClient}
              />
            </Col>
          </Row>
        </div>
      </div>
    </Layout>
  );
}
